import React, {Component, Fragment} from 'react';

import {fieldDefaultProps, fieldDefaultPropTypes} from '../constants';
import PropTypes from 'prop-types';
import { FieldConnect } from 'react-components-form';

import Dropzone from 'dropzone';

import 'dropzone/dist/basic.css';
import 'dropzone/dist/dropzone.css';
import './DropzoneField.scss';

import api from '../../../apiV2/fileSimpleStorageApi';
import * as i18n from "i18next";
import ErrorField from '../ErrorField/ErrorField';

const FSS_URL = process.env.REACT_APP_FSS_URL;

/**
 * Reads the picked file into memory and returns a memory-backed File with the same
 * name and type.
 *
 * Chrome on Android (Samsung Gallery / photo picker, Google Drive) reports an
 * inconsistent modification time for the picked file. Chrome snapshots the file's
 * metadata the first time `size`, `lastModified` or `slice()` is accessed and every
 * later read compares against that snapshot, so uploads fail with
 * net::ERR_UPLOAD_FILE_CHANGED and reads with NotReadableError
 * (https://issues.chromium.org/issues/40123366). Reading the content first, before
 * any metadata access and synchronously in the picker's change event, works on the
 * affected devices. The returned File lives in memory, so Dropzone can slice it into
 * chunks freely.
 *
 * Must be called before anything touches `file.size`, `file.lastModified` or
 * `file.slice()`. Only `name` and `type` are safe to read.
 */
const copyFileIntoMemory = (file) => new Promise((resolve, reject) => {
    if (typeof file.arrayBuffer === 'function') {
        file.arrayBuffer().then(resolve, reject);
        return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
}).then(buffer => {
    const options = {type: file.type, lastModified: Date.now()};
    try {
        return new File([buffer], file.name, options);
    } catch (e) {
        // Browsers without a File constructor: Dropzone only needs name and lastModified
        // on top of a Blob.
        const blob = new Blob([buffer], options);
        blob.name = file.name;
        blob.lastModified = options.lastModified;
        return blob;
    }
});

const logUnreadableFile = (file, err) => {
    console.error('Could not read picked file into memory', err, {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModified,
        lastModifiedDate: new Date(file.lastModified).toISOString(),
        userAgent: navigator.userAgent
    });
};

class DropzoneField extends Component {

    constructor(props) {
        super(props);

        this.dropZone = null;
    }

    state = {
        files: []
    };


    componentDidMount() {
        let access = this.props.private ? 'private' : 'public';
        const {onChange, mimeTypes, maxSize, maxFiles, id, disabled} = this.props;
        const self = this;

        this.dropZone = new Dropzone(id ? '#' + id : '#dropzone', {
            url: FSS_URL + "/chunk_upload",
            withCredentials: true,
            addRemoveLinks: true,
            maxFiles: maxFiles ? maxFiles : 30,
            maxFilesize: maxSize,
            dictDefaultMessage: this.props.defaultMessage ? this.props.defaultMessage : 'Drop files here to upload',
            dictRemoveFile: i18n.t('common:fields.deleteFile'),
            dictMaxFilesExceeded: i18n.t('common:fields.maxFiles'),
            dictInvalidFileType: i18n.t('common:fields.invalidFileType'),
            dictCancelUpload: i18n.t('common:fields.cancelUpload'),
            dictUploadCanceled: i18n.t('common:fields.uploadCanceled'),
            dictFileTooBig: i18n.t('common:fields.maxFileSize', {maxFilesize: maxSize}),
            dictCancelUploadConfirmation: i18n.t('common:messages.areYouSure'),
            acceptedFiles: mimeTypes ? mimeTypes.join(', ') : '',
            accept: function (file, done) {
                if (file.readError) {
                    // The File reference was already stale when it was picked; no upload
                    // attempt can succeed. Show the file with a clear error instead.
                    done(i18n.t('common:fields.fileReadFailed'));
                    return;
                }
                done();
            },
            params: function (files, xhr, chunk) {
                if (chunk) {
                    return {
                        dzuuid: chunk.file.upload.uuid,
                        dzchunkindex: chunk.index,
                        dztotalfilesize: chunk.file.size,
                        dzcurrentchunksize: chunk.dataBlock.data.size,
                        dztotalchunkcount: chunk.file.upload.totalChunkCount,
                        dzchunkbyteoffset: chunk.index * this.options.chunkSize,
                        dzchunksize: this.options.chunkSize,
                        filename: chunk.file.name
                    };
                }
            },
            chunking: true,
            forceChunking: true,
            chunkSize: 2097152,
            parallelChunkUploads: 10,
            chunksUploaded: function (file, done) {
                api.done(file.upload.uuid, file.name, file.upload.totalChunkCount, file.size, access).then(result => {
                    let metadata = result.fileMetaInfo;
                    let replaceMetadata = {
                        latitude: metadata.latitude ? metadata.latitude : "",
                        longitude: metadata.longitude ? metadata.longitude : "",
                        createdDate: metadata.createdDate ? metadata.createdDate : "",
                        model: metadata.model ? metadata.model : "",
                    };

                    result.fileMetaInfo = replaceMetadata;

                    let files = self.state.files;
                    files.push(result);

                    self.setState({files});

                    onChange(files);
                    done();
                });
            },
            removedfile: function(file) {
                file.previewElement.parentNode.removeChild(file.previewElement);

                if (self.state.files.length > 0 && file.accepted) {
                    let files = self.state.files;
                    files.splice(files.findIndex(e => e.uuid === file.upload.uuid), 1);

                    self.setState({
                        files: files
                    });

                    onChange(files);
                    // api.delete(file.upload.uuid);
                }
            }
        });

        // Dropzone's own addFile reads `file.size` first thing, which is exactly what
        // makes Chrome on Android snapshot inconsistent metadata (see copyFileIntoMemory).
        // Intercept every added file (file picker and drag & drop both end up here) and
        // hand Dropzone a memory-backed copy instead of the picked File.
        const addFile = this.dropZone.addFile.bind(this.dropZone);
        this.dropZone.addFile = (file) => {
            copyFileIntoMemory(file).then(addFile, (err) => {
                logUnreadableFile(file, err);
                file.readError = err;
                addFile(file);
            });
        };

        if(disabled) {
            this.dropZone.disable();
        }
    }

    componentWillUnmount() {
        this.dropZone = null;
    }

    UNSAFE_componentWillReceiveProps(nextProps, nextContext) {
        if (nextProps.clean) {
            this.clean();
        }
    }

    clean = () => {
        this.setState({
            files: []
        });

        this.props.onChange([]);
        this.dropZone.removeAllFiles();
    };


    render() {
        const {label, required, hasValidationError, validationErrors, errorStyles, id, showFilename} = this.props;

        return (
            <Fragment>
                {label && <label htmlFor="dropzone">
                    {label}
                    {required && <span className="text-danger"> *</span>}
                </label>}
                <div
                    id={id !== undefined ? id : 'dropzone'}
                    className={"dropzone" + (showFilename ? " dz-show-filename" : "")}>
                </div>
                {hasValidationError &&
                <small className="text-form text-danger">
                    <ErrorField errors={validationErrors} {...errorStyles} />
                </small>}
            </Fragment>

        )
    }
}

DropzoneField.propTypes = {
    ...fieldDefaultProps,
    clean: PropTypes.any
};

DropzoneField.defaultProps = {
    ...fieldDefaultPropTypes,
    clean: PropTypes.any,
};

export default FieldConnect(DropzoneField);
